import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { Search } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { salesService } from '../../services/salesService';
import { DataGrid, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';

export default function AllSales() {
  const [salesData, setSalesData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('all');
  const { language } = useLanguage();
  const { user } = useAuth();

  // Traductions
  const text = {
    fr: {
      allSales: 'Toutes les Ventes',
      search: 'Rechercher une vente...',
      date: 'Date',
      seller: 'Vendeur',
      products: 'Produits',
      total: 'Total',
      paymentMode: 'Mode de paiement',
      status: 'Statut',
      loading: 'Chargement...',
      error: 'Erreur lors du chargement des ventes',
      filterBy: 'Filtrer par',
      all: 'Tout',
      completed: 'Complétée',
      pending: 'En attente',
    },
    en: {
      allSales: 'All Sales',
      search: 'Search sales...',
      date: 'Date',
      seller: 'Seller',
      products: 'Products',
      total: 'Total',
      paymentMode: 'Payment Mode',
      status: 'Status',
      loading: 'Loading...',
      error: 'Error loading sales',
      filterBy: 'Filter by',
      all: 'All',
      completed: 'Completed',
      pending: 'Pending',
    }
  };

  const currentText = text[language as keyof typeof text];

  useEffect(() => {
    const fetchSales = async () => {
      if (user?.companyId) {
        setLoading(true);
        try {
          const data = await salesService.getSales(user.companyId);
          setSalesData(data);
        } catch (error) {
          console.error('Error fetching sales:', error);
          setError(currentText.error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchSales();
  }, [user?.companyId]);

  const handleFilterChange = (event: SelectChangeEvent) => {
    setFilterField(event.target.value);
  };

  const columns: GridColDef[] = [
    { 
      field: '__index',
      headerName: 'N°', 
      width: 70,
      renderCell: (params) => {
        return filteredSales.indexOf(params.row) + 1;
      }
    },
    { 
      field: 'createdAt', 
      headerName: currentText.date, 
      width: 120,
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => 
        new Date(params.row.createdAt).toLocaleDateString()
    },
    { 
      field: 'seller', 
      headerName: currentText.seller, 
      width: 150,
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => 
        params.row.userId?.username || 'N/A'
    },
    { 
      field: 'products', 
      headerName: currentText.products, 
      width: 250,
      flex: 1.5,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {params.row.products?.map((product: any) => (
            product.productId ? 
              `${product.productId.name} (${product.quantity}x)\n` : 
              'Produit supprimé\n'
          )).join('')}
        </div>
      )
    },
    { 
      field: 'totalAmount', 
      headerName: currentText.total, 
      width: 130,
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => 
        `${params.row.totalAmount} ${user?.company?.signCurrency || 'FC'}`
    },
    { 
      field: 'paymentMode', 
      headerName: currentText.paymentMode, 
      width: 150,
      flex: 1
    },
    { 
      field: 'status', 
      headerName: currentText.status, 
      width: 130,
      flex: 1,
      renderCell: (params) => (
        <span
          style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '0.875rem',
            backgroundColor: params.row.status === 'completed' ? '#e6ffe6' : '#fff3e6',
            color: params.row.status === 'completed' ? '#006600' : '#cc7700'
          }}
        >
          {params.row.status}
        </span>
      )
    }
  ];

  const filteredSales = salesData.filter(sale => {
    const matchesSearch = 
      sale.userId?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.paymentMode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.status?.toLowerCase().includes(searchTerm.toLowerCase());

    if (filterField === 'all') return matchesSearch;
    return matchesSearch && sale.status === filterField;
  });

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Typography variant="h4" gutterBottom>
        {currentText.allSales}
      </Typography>
      <Typography color="text.secondary">
        Gérez toutes vos ventes
      </Typography>

          {/* Filtres */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={currentText.search}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{currentText.filterBy}</InputLabel>
              <Select
                value={filterField}
                label={currentText.filterBy}
                onChange={handleFilterChange}
              >
                <MenuItem value="all">{currentText.all}</MenuItem>
                <MenuItem value="completed">{currentText.completed}</MenuItem>
                <MenuItem value="pending">{currentText.pending}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Liste des ventes */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <Box sx={{ height: 'calc(100vh - 250px)', width: '100%' }}>
              <DataGrid
                rows={filteredSales}
                columns={columns}
                getRowId={(row) => row._id}
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                disableRowSelectionOnClick
                sx={{
                  backgroundColor: 'white',
                  '& .MuiDataGrid-cell': {
                    borderColor: '#f0f0f0',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#fafafa',
                    borderBottom: '2px solid #f0f0f0',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
            </Box>
          )}
    </Box>
  );
}
